import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from "@microsoft/signalr";
import type { ApiInAppNotification } from "@tm-food/api-contract";
import { getApiBaseUrl } from "@/app/config/env";
import type { TokenStore } from "@/shared/auth/tokenStore";
import { mapInAppNotification, type InAppNotification } from "@/modules/sys/model/types";

export const NOTIFICATION_RECEIVED = "notificationReceived";

export type NotificationReceivedHandler = (n: InAppNotification) => void;

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

/** Coerce SignalR payload (camelCase DTO) into module model. */
export function parseRealtimeNotification(payload: unknown): InAppNotification | null {
  if (!isRecord(payload)) return null;
  const id = payload.id;
  const eventType = payload.eventType;
  const title = payload.title;
  const body = payload.body;
  const severity = payload.severity;
  const createdAt = payload.createdAt;
  if (
    typeof id !== "string" ||
    typeof eventType !== "string" ||
    typeof title !== "string" ||
    typeof body !== "string" ||
    typeof severity !== "string" ||
    typeof createdAt !== "string"
  ) {
    return null;
  }

  const dto: ApiInAppNotification = {
    id,
    eventType,
    title,
    body,
    severity,
    entityType: typeof payload.entityType === "string" ? payload.entityType : null,
    entityId: typeof payload.entityId === "string" ? payload.entityId : null,
    plantCode: typeof payload.plantCode === "string" ? payload.plantCode : null,
    routeHint: typeof payload.routeHint === "string" ? payload.routeHint : null,
    createdAt,
    readAt: typeof payload.readAt === "string" ? payload.readAt : null,
    isRead: payload.isRead === true,
  };
  return mapInAppNotification(dto);
}

export function createNotificationHubConnection(tokenStore: TokenStore): HubConnection {
  return new HubConnectionBuilder()
    .withUrl(`${getApiBaseUrl()}/hubs/notifications`, {
      accessTokenFactory: () => tokenStore.get()?.accessToken ?? "",
    })
    .withAutomaticReconnect()
    .configureLogging(LogLevel.Warning)
    .build();
}

export async function startNotificationHub(
  connection: HubConnection,
): Promise<void> {
  if (connection.state === HubConnectionState.Connected) return;
  if (connection.state === HubConnectionState.Connecting) return;
  await connection.start();
}

export async function stopNotificationHub(
  connection: HubConnection,
): Promise<void> {
  if (connection.state === HubConnectionState.Disconnected) return;
  await connection.stop();
}
